/**
 * variant-tabs-updater.js
 * Actualiza dinámicamente los tabs de información nutricional, composición, 
 * aminograma e ingredientes cuando cambia la variante del producto.
 * 
 * @version 1.1.0
 */

(function() {
  'use strict';

  // Esperar a que el DOM esté listo
  document.addEventListener('DOMContentLoaded', function() {
    initVariantTabsUpdater();
    // Inicializar visibilidad de tabs al cargar
    initTabsVisibility();
  });

  function initVariantTabsUpdater() {
    // Escuchar el evento de cambio de variante del tema
    document.addEventListener('variant:changed', handleVariantChange);
    
    // También escuchar el evento del sistema de Minimog
    if (typeof MinimogEvents !== 'undefined' && typeof MinimogTheme !== 'undefined') {
      MinimogEvents.subscribe(
        MinimogTheme.pubSubEvents.variantChange,
        function(event) {
          if (event.data && event.data.variant) {
            handleVariantChange({ detail: { variant: event.data.variant } });
          }
        }
      );
    }
  }

  /**
   * Inicializa la visibilidad de los tabs al cargar la página
   */
  function initTabsVisibility() {
    var tabTypes = ['nutrition', 'composicion', 'aminograma', 'ingredientes'];
    
    tabTypes.forEach(function(tabType) {
      var content = document.querySelector('[data-variant-tab="' + tabType + '"]');
      if (content) {
        var parentCollapsible = content.closest('collapsible-tab');
        if (parentCollapsible) {
          // Solo comprobar el style inline, NO getComputedStyle,
          // porque el elemento está dentro de un acordeón colapsado (hidden)
          // y getComputedStyle siempre devolvería 'none'.
          var isExplicitlyHidden = content.style.display === 'none';
          if (isExplicitlyHidden) {
            parentCollapsible.style.display = 'none';
          }
        }
      } else {
        hideCollapsibleIfEmpty(tabType);
      }
    });
  }

  /**
   * Oculta un collapsible tab SOLO si su contenido está realmente vacío.
   * Busca por heading y comprueba si hay contenido HTML real dentro.
   */
  function hideCollapsibleIfEmpty(tabType) {
    var headingMap = {
      'nutrition': ['información nutricional', 'info. nutricional', 'info nutricional'],
      'composicion': ['composición'],
      'aminograma': ['aminograma'],
      'ingredientes': ['ingredientes']
    };
    
    var headings = headingMap[tabType] || [];
    var collapsibles = document.querySelectorAll('collapsible-tab');
    
    collapsibles.forEach(function(collapsible) {
      var button = collapsible.querySelector('.m-collapsible--button');
      if (button) {
        var headingText = button.textContent.trim().toLowerCase();
        if (headings.some(function(h) { return headingText.includes(h); })) {
          var contentInner = collapsible.querySelector('.m-collapsible--content__inner');
          var hasRealContent = contentInner && contentInner.innerHTML.trim().length > 0 
            && contentInner.querySelector('div, p, table, ul, ol, img, span:not(.m-collapsible--icon)');
          if (!hasRealContent) {
            collapsible.style.display = 'none';
          }
        }
      }
    });
  }

  /**
   * Oculta un collapsible tab basándose en el tipo de tab (forzado, sin comprobar contenido)
   */
  function hideCollapsibleByTabType(tabType) {
    var headingMap = {
      'nutrition': ['información nutricional', 'info. nutricional', 'info nutricional'],
      'composicion': ['composición'],
      'aminograma': ['aminograma'],
      'ingredientes': ['ingredientes']
    };
    
    var headings = headingMap[tabType] || [];
    var collapsibles = document.querySelectorAll('collapsible-tab');
    
    collapsibles.forEach(function(collapsible) {
      var button = collapsible.querySelector('.m-collapsible--button');
      if (button) {
        var headingText = button.textContent.trim().toLowerCase();
        if (headings.some(function(h) { return headingText.includes(h); })) {
          collapsible.style.display = 'none';
        }
      }
    });
  }

  function handleVariantChange(event) {
    const variant = event.detail?.variant;
    if (!variant || !variant.id) {
      return;
    }

    // Obtener el productId del producto actual
    const productInfo = document.querySelector('product-info');
    const productId = productInfo?.dataset?.productId;
    
    if (!productId) {
      return;
    }

    // Obtener los metafields de la variante
    const variantMetafields = window._variantMetafields?.[productId]?.[variant.id];
    const productMetafields = window._productMetafields?.[productId];
    
    if (!variantMetafields) {
      hideEmptyTabs();
      // Fallback: si hay ingredientes a nivel de producto, mostrarlos
      if (productMetafields?.ingredientes) {
        updateIngredientesTab(productMetafields.ingredientes);
      }
      return;
    }

    // Actualizar cada sección
    updateNutritionTab(variantMetafields.nutrition);
    updateComposicionTab(variantMetafields.composicion);
    updateAminogramaTab(variantMetafields.aminograma);
    // Ingredientes: variante primero, fallback a producto
    var ingredientesData = variantMetafields.ingredientes || productMetafields?.ingredientes;
    updateIngredientesTab(ingredientesData);
  }

  /**
   * Actualiza la tabla de información nutricional
   */
  function updateNutritionTab(nutritionData) {
    const container = document.querySelector('[data-variant-tab="nutrition"]');
    const collapsibleContent = document.querySelector('.collapsible-nutrition-content');
    
    if (!container && !collapsibleContent) {
      hideCollapsibleByTabType('nutrition');
      return;
    }

    const targetContainer = container || collapsibleContent;
    const parentCollapsible = targetContainer.closest('collapsible-tab');

    if (!nutritionData) {
      targetContainer.style.display = 'none';
      if (parentCollapsible) {
        parentCollapsible.style.display = 'none';
      }
      return;
    }

    targetContainer.style.display = '';
    if (parentCollapsible) {
      parentCollapsible.style.display = '';
    }

    let rawData;
    try {
      rawData = typeof nutritionData === 'string' ? JSON.parse(nutritionData) : nutritionData;
      if (typeof rawData === 'string') rawData = JSON.parse(rawData);
    } catch (e) {
      console.error('[VariantTabsUpdater] Error parseando datos de nutrición:', e);
      return;
    }

    let portions, footerLines;
    if (rawData && rawData.porciones && Array.isArray(rawData.porciones)) {
      portions = rawData.porciones;
      footerLines = rawData.footer || [];
    } else if (Array.isArray(rawData)) {
      portions = rawData;
      footerLines = [];
    } else {
      return;
    }

    if (!portions || portions.length === 0) return;

    const dataScript = targetContainer.querySelector('.nutrition-data');
    if (dataScript) {
      dataScript.textContent = JSON.stringify(rawData);
    }

    renderNutritionFooter(targetContainer, footerLines);

    const tabEl = targetContainer.closest('collapsible-tab');
    if (tabEl && window.initNutritionSelector) {
      window.initNutritionSelector(tabEl);
    } else if (portions[0].valores && Array.isArray(portions[0].valores)) {
      const tbody = targetContainer.querySelector('.product-nutrition-table tbody');
      if (tbody) {
        tbody.innerHTML = '';
        portions[0].valores.forEach(function(item) {
          var tr = document.createElement('tr');
          tr.innerHTML = '<th class="nutrition-main">' + item.nombre + '</th>' +
            '<td>' + (item.valor || '—') + '</td>';
          tbody.appendChild(tr);
          if (item.sub && Array.isArray(item.sub)) {
            item.sub.forEach(function(subItem) {
              var subTr = document.createElement('tr');
              subTr.innerHTML = '<th class="nutrition-sub">' + subItem.nombre + '</th>' +
                '<td>' + (subItem.valor || '—') + '</td>';
              tbody.appendChild(subTr);
            });
          }
        });
      }
    } else {
      const cells = targetContainer.querySelectorAll('.nut-cell');
      cells.forEach(function(cell) {
        const key = cell.getAttribute('data-key');
        cell.textContent = (key && portions[0][key]) || '—';
      });
    }
  }

  /**
   * Formatea el nombre de un sub-ingrediente para "Del cual …":
   * – Si el nombre es todo mayúsculas (ej. "EGCG") lo mantiene.
   * – Si no, pone la primera letra en minúscula.
   */
  function formatSubIngredientName(name) {
    if (name === name.toUpperCase()) return name;
    return name.charAt(0).toLowerCase() + name.slice(1);
  }

  /**
   * Renderiza el footer dinámico desde un array de líneas del JSON.
   * Cada línea se formatea: la parte antes de ": " en bold+italic.
   */
  function renderDynamicFooter(container, footerLines, cssClass) {
    var el = container.querySelector('.' + cssClass);
    if (!footerLines || !Array.isArray(footerLines) || footerLines.length === 0) {
      if (el) el.style.display = 'none';
      return;
    }
    if (!el) {
      el = document.createElement('p');
      el.className = cssClass;
      var inner = container.querySelector('.product-nutrition__inner') ||
                  container.querySelector('.product-composicion__inner') ||
                  container.querySelector('.product-aminograma__inner');
      if (inner) inner.appendChild(el);
    }
    el.style.display = '';
    el.innerHTML = footerLines.map(function(line) {
      if (line.indexOf('DDR') !== -1 || line.indexOf('VRN') !== -1) {
        var idx = line.indexOf(': ');
        if (idx === -1) return '<em>' + line + '</em>';
        return '<em><strong>' + line.substring(0, idx) + '</strong>: ' + line.substring(idx + 2) + '</em>';
      }
      return line;
    }).join('<br>');
  }

  function renderNutritionFooter(container, footerLines) {
    renderDynamicFooter(container, footerLines, 'nutrition-footer');
  }

  function renderComposicionFooter(container, footerLines) {
    renderDynamicFooter(container, footerLines, 'composicion-footer');
  }

  function renderAminogramaFooter(container, footerLines) {
    renderDynamicFooter(container, footerLines, 'aminograma-footer');
  }

  /**
   * Actualiza la tabla de composición (2 columnas)
   */
  function updateComposicionTab(composicionData) {
    const container = document.querySelector('[data-variant-tab="composicion"]');
    const collapsibleContent = document.querySelector('.collapsible-composicion-content');
    
    if (!container && !collapsibleContent) {
      hideCollapsibleByTabType('composicion');
      return;
    }

    const targetContainer = container || collapsibleContent;
    const parentCollapsible = targetContainer.closest('collapsible-tab');

    if (!composicionData) {
      targetContainer.style.display = 'none';
      if (parentCollapsible) {
        parentCollapsible.style.display = 'none';
      }
      return;
    }

    targetContainer.style.display = '';
    if (parentCollapsible) {
      parentCollapsible.style.display = '';
    }

    let data;
    try {
      data = typeof composicionData === 'string' ? JSON.parse(composicionData) : composicionData;
    } catch (e) {
      console.error('[VariantTabsUpdater] Error parseando datos de composición:', e);
      return;
    }

    // Actualizar el JSON embebido para el selector de porciones
    const dataScript = targetContainer.querySelector('.composicion-data');
    if (dataScript) {
      dataScript.textContent = JSON.stringify(data);
    }

    // Si hay porciones, delegar al selector JS
    if (data.porciones && Array.isArray(data.porciones) && data.porciones.length > 0) {
      const tabEl = targetContainer.closest('collapsible-tab');
      if (tabEl && window.initComposicionSelector) {
        window.initComposicionSelector(tabEl);
      }
      renderComposicionFooter(targetContainer, data.footer);
      return;
    }

    // Sin porciones: ocultar selector si existe y renderizar directamente
    const existingSelector = parentCollapsible?.querySelector('.composicion-selector');
    if (existingSelector) existingSelector.remove();

    // Actualizar footer desde JSON
    renderComposicionFooter(targetContainer, data.footer);

    const composicion_por_dosis = data.composicion_por_dosis;
    if (!composicion_por_dosis || !Array.isArray(composicion_por_dosis)) {
      return;
    }

    const tbody = targetContainer.querySelector('.product-composicion-table tbody');
    if (!tbody) return;

    tbody.innerHTML = '';

    composicion_por_dosis.forEach(function(item) {
      if (item.dosis) {
        var groupTr = document.createElement('tr');
        groupTr.className = 'composicion-group-row';
        groupTr.innerHTML = '<td colspan="2"><span class="composicion-group-content">' +
          '<strong>' + item.ingrediente + '</strong>' +
          '<span class="composicion-group-dosis"><strong>' + item.dosis + '</strong></span>' +
          '</span></td>';
        tbody.appendChild(groupTr);

        if (item.de_las_cuales && Array.isArray(item.de_las_cuales)) {
          var itemPrefijo = item.prefijo || 'Del cual';
          item.de_las_cuales.forEach(function(child) {
            var tr = document.createElement('tr');
            tr.innerHTML = '<td>' + child.ingrediente + '</td>' +
              '<td>' + (child.cantidad || '') + '</td>';
            tbody.appendChild(tr);

            if (child.de_las_cuales && Array.isArray(child.de_las_cuales)) {
              var childPrefijo = child.prefijo || itemPrefijo;
              child.de_las_cuales.forEach(function(sub) {
                var subTr = document.createElement('tr');
                subTr.className = 'composicion-sub-row';
                subTr.innerHTML = '<td>' + childPrefijo + ' ' + formatSubIngredientName(sub.ingrediente) + '</td>' +
                  '<td>' + (sub.cantidad || '') + '</td>';
                tbody.appendChild(subTr);
              });
            }
          });
        }
      } else {
        var tr = document.createElement('tr');
        tr.innerHTML = '<td>' + item.ingrediente + '</td>' +
          '<td>' + (item.cantidad || '') + '</td>';
        tbody.appendChild(tr);

        if (item.de_las_cuales && Array.isArray(item.de_las_cuales)) {
          var prefijo = item.prefijo || 'Del cual';
          item.de_las_cuales.forEach(function(sub) {
            var subTr = document.createElement('tr');
            subTr.className = 'composicion-sub-row';
            subTr.innerHTML = '<td>' + prefijo + ' ' + formatSubIngredientName(sub.ingrediente) + '</td>' +
              '<td>' + (sub.cantidad || '') + '</td>';
            tbody.appendChild(subTr);
          });
        }
      }
    });
  }

  /**
   * Actualiza la tabla de aminograma
   */
  function updateAminogramaTab(aminogramaData) {
    const container = document.querySelector('[data-variant-tab="aminograma"]');
    const collapsibleContent = document.querySelector('.collapsible-aminograma-content');
    
    if (!container && !collapsibleContent) {
      hideCollapsibleByTabType('aminograma');
      return;
    }

    const targetContainer = container || collapsibleContent;
    const parentCollapsible = targetContainer.closest('collapsible-tab');

    if (!aminogramaData) {
      targetContainer.style.display = 'none';
      if (parentCollapsible) {
        parentCollapsible.style.display = 'none';
      }
      return;
    }

    targetContainer.style.display = '';
    if (parentCollapsible) {
      parentCollapsible.style.display = '';
    }

    let data;
    try {
      data = typeof aminogramaData === 'string' ? JSON.parse(aminogramaData) : aminogramaData;
    } catch (e) {
      console.error('[VariantTabsUpdater] Error parseando datos de aminograma:', e);
      return;
    }

    // Actualizar el JSON embebido para el selector de porciones
    const dataScript = targetContainer.querySelector('.aminograma-data');
    if (dataScript) {
      dataScript.textContent = JSON.stringify(data);
    }

    // Si hay porciones, delegar al selector JS
    if (data.porciones && Array.isArray(data.porciones) && data.porciones.length > 0) {
      const tabEl = targetContainer.closest('collapsible-tab');
      if (tabEl && window.initAminogramaSelector) {
        window.initAminogramaSelector(tabEl);
      }
      renderAminogramaFooter(targetContainer, data.footer);
      return;
    }

    // Sin porciones: ocultar selector si existe, actualizar footer
    const existingSelector = parentCollapsible?.querySelector('.aminograma-selector');
    if (existingSelector) existingSelector.remove();
    renderAminogramaFooter(targetContainer, data.footer);

    let amino_acids = data.aminograma || data.valores;
    if (amino_acids && Array.isArray(amino_acids) && amino_acids.length > 0 && amino_acids[0] && amino_acids[0].nombre !== undefined) {
      amino_acids = amino_acids.map(function(v) { return [v.nombre, v.valor || '—']; });
    } else if (amino_acids && !Array.isArray(amino_acids) && typeof amino_acids === 'object') {
      amino_acids = Object.entries(amino_acids);
    }
    if (!amino_acids || !Array.isArray(amino_acids)) {
      return;
    }

    const rowsContainer = targetContainer.querySelector('.aminograma-rows');
    if (!rowsContainer) return;

    rowsContainer.innerHTML = '';

    let amino_count = 0;
    let currentRow = null;

    amino_acids.forEach(function(amino_acid, index) {
      amino_count++;
      const is_odd = amino_count % 2 === 1;

      if (is_odd) {
        currentRow = document.createElement('div');
        currentRow.className = 'aminograma-row';
        const col = document.createElement('div');
        col.className = 'aminograma-col';
        col.innerHTML = '<span class="aminograma-name">' + amino_acid[0] + '</span>' +
                        '<span class="aminograma-value">' + amino_acid[1] + '</span>';
        currentRow.appendChild(col);
      } else {
        const col = document.createElement('div');
        col.className = 'aminograma-col';
        col.innerHTML = '<span class="aminograma-name">' + amino_acid[0] + '</span>' +
                        '<span class="aminograma-value">' + amino_acid[1] + '</span>';
        currentRow.appendChild(col);
        rowsContainer.appendChild(currentRow);
        currentRow = null;
      }

      if (is_odd && index === amino_acids.length - 1) {
        const emptyCol = document.createElement('div');
        emptyCol.className = 'aminograma-col empty';
        currentRow.appendChild(emptyCol);
        rowsContainer.appendChild(currentRow);
      }
    });
  }

  /**
   * Actualiza el contenido de ingredientes
   */
  function updateIngredientesTab(ingredientesData) {
    const container = document.querySelector('[data-variant-tab="ingredientes"]');
    const collapsibleContent = document.querySelector('.collapsible-ingredientes-content');
    
    if (!container && !collapsibleContent) {
      hideCollapsibleByTabType('ingredientes');
      return;
    }

    const targetContainer = container || collapsibleContent;
    const parentCollapsible = targetContainer.closest('collapsible-tab');

    if (!ingredientesData) {
      targetContainer.style.display = 'none';
      if (parentCollapsible) {
        parentCollapsible.style.display = 'none';
      }
      return;
    }

    targetContainer.style.display = '';
    if (parentCollapsible) {
      parentCollapsible.style.display = '';
    }

    const html = normalizeIngredientesData(ingredientesData);
    if (!html) {
      targetContainer.style.display = 'none';
      if (parentCollapsible) parentCollapsible.style.display = 'none';
      return;
    }

    const ingredientesDiv = targetContainer.querySelector('.ingredientes');
    if (ingredientesDiv) {
      ingredientesDiv.innerHTML = html;
    } else {
      targetContainer.innerHTML = '<div class="ingredientes">' + html + '</div>';
    }
  }

  /**
   * Normaliza el dato de ingredientes para devolver SIEMPRE un string HTML.
   * Soporta:
   *  - string ya HTML (lo más habitual cuando se pre-renderiza con metafield_tag)
   *  - objeto Metafield completo ({ value, type, ... })
   *  - JSON serializado de un Metafield
   *  - rich_text JSON de Shopify ({ type: 'root', children: [...] })
   */
  function normalizeIngredientesData(data) {
    if (!data) return '';

    if (typeof data === 'string') {
      var trimmed = data.trim();
      if (trimmed.charAt(0) === '{' || trimmed.charAt(0) === '[') {
        try { return normalizeIngredientesData(JSON.parse(trimmed)); }
        catch (e) { return data; }
      }
      return data;
    }

    if (typeof data === 'object') {
      if (data.value !== undefined) return normalizeIngredientesData(data.value);
      if (data.type === 'root' && Array.isArray(data.children)) {
        return renderRichText(data.children);
      }
      if (Array.isArray(data)) return renderRichText(data);
    }

    return '';
  }

  /**
   * Render mínimo del JSON de rich_text de Shopify a HTML.
   * Cubre los nodos típicos usados en el campo de ingredientes.
   */
  function renderRichText(nodes) {
    if (!Array.isArray(nodes)) return '';
    return nodes.map(function(node) {
      if (!node) return '';
      if (node.type === 'text') {
        var text = escapeHtml(node.value || '');
        if (node.bold) text = '<strong>' + text + '</strong>';
        if (node.italic) text = '<em>' + text + '</em>';
        return text;
      }
      var inner = renderRichText(node.children || []);
      switch (node.type) {
        case 'paragraph':   return '<p>' + inner + '</p>';
        case 'heading':     var lvl = node.level || 2; return '<h' + lvl + '>' + inner + '</h' + lvl + '>';
        case 'list':        return (node.listType === 'ordered' ? '<ol>' : '<ul>') + inner + (node.listType === 'ordered' ? '</ol>' : '</ul>');
        case 'list-item':   return '<li>' + inner + '</li>';
        case 'link':        return '<a href="' + escapeHtml(node.url || '#') + '"' + (node.title ? ' title="' + escapeHtml(node.title) + '"' : '') + (node.target ? ' target="' + escapeHtml(node.target) + '"' : '') + '>' + inner + '</a>';
        default:            return inner;
      }
    }).join('');
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Oculta los tabs que no tienen datos para la variante actual
   */
  function hideEmptyTabs() {
    const tabTypes = ['nutrition', 'composicion', 'aminograma', 'ingredientes'];
    
    tabTypes.forEach(function(tabType) {
      const container = document.querySelector('[data-variant-tab="' + tabType + '"]');
      if (container) {
        container.style.display = 'none';
        const parentCollapsible = container.closest('collapsible-tab');
        if (parentCollapsible) {
          parentCollapsible.style.display = 'none';
        }
      } else {
        hideCollapsibleByTabType(tabType);
      }
    });
  }

})();
